/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  Grid,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  TableBody,
  Box,
  Checkbox,
  Alert,
} from "@mui/material";
import FormTitle from "../../../form-fields/form-title";
import TextInput from "../../../form-fields/text-input";
import SelectInput from "../../../form-fields/select-input";
import DashBoardContainer from "../../../layout/dashboard-container";
import {
  FormLabel,
  FormValue,
  GridRow,
} from "../generate-receipt/custom-form-grid";
import { useSelector } from "react-redux";
import { labels } from "../../../../lang/labels";
import { useFormik, FormikProvider, Form } from "formik";
import {
  getEDCTxnStatusSchema,
  reprintReceiptValidationSchema,
} from "../../../../utils/validation-schema";
import { getReceiptList } from "../../../../services/generate-counter-receipt";
import { useNavigate } from "react-router-dom";
import { Print } from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FormButtons from "../buttons";
import DateInput from "../../../form-fields/date-picker";
import { getCurrentDate } from "../../../../utils/helpers";
import {
  getEdcTransactionDetails,
  getEdcTransactionDetailsAndGenerateReceipt,
} from "../../../../services/edc-service";
import CheckBoxInput from "../../../form-fields/checkbox-input";
import useApiState from "../../common/useApiState";
import Loader from "../../../loader/loader";
import AlertMsg from "../../../common/alert";

const GetEdcTransactionStatus = () => {
  const navigate = useNavigate();
  const initialState = {
    propertyCode: "",
    transactionDate: getCurrentDate(),
  };
  const [edcTransactionData, setEdcTransactionData] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { loading, setLoading, error, setError, success, setSuccess } =
    useApiState();
  const lang = useSelector((state) => state.userDetails.lang);
  const formik = useFormik({
    initialValues: initialState,
    validationSchema: getEDCTxnStatusSchema,
  });

  const handleSubmitButtonClick = async (e) => {
    e.preventDefault();
    if (formik.values.propertyCode && formik.values.transactionDate) {
      try {
        setLoading(true);
        const response = await getEdcTransactionDetails(formik.values);
        setEdcTransactionData(response);
      } catch (error) {
        setError("Something went wrong, try again later");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle "Select All" checkbox
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      const allIds = edcTransactionData.map((item) => item.transactionId);
      setSelectedTransactions(allIds);
    } else {
      setSelectedTransactions([]);
    }
  };

  // Handle individual checkbox
  const handleCheckboxChange = (transactionId) => {
    setSelectedTransactions((prevSelected) =>
      prevSelected.includes(transactionId)
        ? prevSelected.filter((id) => id !== transactionId)
        : [...prevSelected, transactionId]
    );
  };

  // Handle save button click (gathering the selected transactions)
  const handleSave = async () => {
    const selectedData = edcTransactionData.filter((item) =>
      selectedTransactions.includes(item.transactionId)
    );
    // Perform save operation with selectedData
    if (selectedData.length) {
      try {
        setLoading(true);
        const response = await getEdcTransactionDetailsAndGenerateReceipt(
          selectedData
        );
        if (response.propertyBillReceiptNumber) {
          setSuccess("Success");
          setTimeout(() => {
            navigate(
              `/PrintReceipt?receiptNo=${response.propertyBillReceiptNumber}&receiptType=BillReceipt`
            );
          }, 1000);
        } else {
          setError("Something went wrong on generate receipt, try again later");
        }
      } catch (error) {
        setError("Something went wrong, try again later");
      } finally {
        setLoading(false);
      }
    } else {
      setError("Select atleast one transaction to save.");
    }
  };

  const handleTableReset = () => {
    setSelectedTransactions([]);
    setSelectAll(false);
  };

  const resetStateData = () => {
    window.location.reload();
  };

  return (
    <DashBoardContainer>
      <FormikProvider value={formik}>
        <Form>
          {success && <Alert severity="success">{success}</Alert>}
          {loading && <Loader />}
          {error && (
            <AlertMsg
              message={error}
              severity="error"
              onClose={() => {
                setError("");
              }}
            />
          )}
          <Paper elevation={4} sx={{ marginBottom: "15px" }}>
            <FormTitle title={labels.GetEDCTransactionStatus[lang]} />
            <Grid>
              <GridRow>
                <FormLabel label={labels.PropertyCode[lang]} required />
                <FormValue
                  component={<TextInput name="propertyCode" required />}
                />
                <FormLabel label={labels.transactionDate[lang]} required />
                <FormValue
                  component={<DateInput name="transactionDate" required />}
                />
              </GridRow>
              <Grid container justifyContent="center" alignItems="center">
                <Grid
                  item
                  md={3}
                  container
                  justifyContent={{ md: "flex-end" }}
                  alignItems="center"
                  p={2}
                >
                  <FormButtons
                    isValid={!(formik.isValid && formik.dirty)}
                    handleSubmitButtonClick={handleSubmitButtonClick}
                    resetForm={() => {
                      formik.resetForm();
                      resetStateData();
                    }}
                    submitBtnLabel="Show"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <GridRow>
            {edcTransactionData && edcTransactionData.length ? (
              <TableContainer style={{ padding: "50px" }}>
                <Table
                  size="small"
                  sx={{ minWidth: 600 }}
                  aria-label="simple table"
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        background: "#e6eaeb",
                        "&:last-child td, &:last-child th": {
                          border: "1px solid black",
                        },
                      }}
                    >
                      <TableCell align="center">{labels.SrNo[lang]}</TableCell>
                      <TableCell align="center">
                        {labels.PropertyCode[lang]}
                      </TableCell>
                      <TableCell align="center">
                        {labels.ConsumerName[lang]}
                      </TableCell>
                      <TableCell align="center">
                        {labels.transactionDate[lang]}
                      </TableCell>
                      <TableCell align="center">
                        {labels.PlutusTxnRefID[lang]}
                      </TableCell>
                      <TableCell align="center">
                        {labels.Amount[lang]}
                      </TableCell>
                      <TableCell align="center">
                        Select All
                        <Checkbox
                          name="selectAll"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {edcTransactionData.map((item, index) => {
                      return (
                        <TableRow key={index}>
                          <TableCell align="center" className="cellBorder">
                            {index + 1}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            {item.propertyCode}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            {item.propertyName}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            {item.transactionDate}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            {item.transactionId}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            {item.transactionAmount}
                          </TableCell>
                          <TableCell align="center" className="cellBorder">
                            <Checkbox
                              name={item.transactionId}
                              checked={selectedTransactions.includes(
                                item.transactionId
                              )}
                              onChange={() =>
                                handleCheckboxChange(item.transactionId)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <></>
            )}
          </GridRow>
          {edcTransactionData && edcTransactionData.length ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                size="small"
                sx={{ margin: "5px" }}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{ margin: "5px" }}
                onClick={handleTableReset}
              >
                Reset
              </Button>
            </Box>
          ) : (
            <></>
          )}
        </Form>
      </FormikProvider>
    </DashBoardContainer>
  );
};
export default GetEdcTransactionStatus;
